import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  LayoutDashboard, ShoppingBag, Sparkles, HeartHandshake, FileText, Users,
  Settings as SettingsIcon, LogOut, X, RefreshCw, CheckCircle2, StickyNote, Zap,
  ShieldCheck, Plug, MessageSquareWarning, Undo2, ClipboardCheck, Mail,
} from "lucide-react";
import api, { API, eur, setAdminToken, getAdminToken, clearAdminToken } from "../api";
import { usePageTitle } from "../components/common";

function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      setAdminToken(data.access_token);
      onLogin();
    } catch {
      toast.error("Identifiants incorrects");
    } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-grain flex items-center justify-center px-5">
      <form onSubmit={submit} className="card-soft p-8 w-full max-w-sm" data-testid="admin-login-form">
        <div className="text-center mb-6">
          <img src="/brand-logo.png" alt="Logo" className="w-14 h-14 rounded-full mx-auto" />
          <h1 className="mt-4 font-serif text-2xl text-[#5A3A4B]">Administration</h1>
          <p className="text-xs text-[#7A5D58] mt-1">La Maison de ta Sorcière Préférée</p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="field-label">Email</label>
            <input required type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
          </div>
          <div>
            <label className="field-label">Mot de passe</label>
            <input required type="password" className="field-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="admin-login-btn">
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="card-soft p-5">
      <div className="text-xs text-[#7A5D58] mb-1">{label}</div>
      <div className={`font-serif text-2xl font-semibold ${accent ? "text-[#C9A56A]" : "text-[#5A3A4B]"}`}>{value}</div>
    </div>
  );
}

function AdminPhoto({ fileId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke;
    if (!fileId) return;
    fetch(`${API}/files/${fileId}`, { headers: { Authorization: `Bearer ${getAdminToken()}` } })
      .then((r) => (r.ok ? r.blob() : Promise.reject()))
      .then((b) => { revoke = URL.createObjectURL(b); setUrl(revoke); })
      .catch(() => setUrl(false));
    return () => revoke && URL.revokeObjectURL(revoke);
  }, [fileId]);
  if (url === false) return <p className="text-xs text-[#a58f92]">Photo supprimée (conservation expirée).</p>;
  if (!url) return <div className="w-full h-40 rounded-xl bg-[#F6EFE7] animate-pulse" />;
  return <img src={url} alt="Photo cliente" className="rounded-xl max-h-64 object-contain border border-[#7A5D58]/10" />;
}

function OrderDetail({ orderId, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/orders/${orderId}`).then((r) => setD(r.data));
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    await api.post(`/admin/orders/${orderId}/status`, { status });
    toast.success("Statut mis à jour");
    load(); onChanged && onChanged();
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/orders/${orderId}/notes`, { note });
    setNote(""); toast.success("Note ajoutée"); load();
  };
  const retryInvoice = async () => {
    const { data } = await api.post(`/admin/invoices/${orderId}/retry`);
    if (data.ok) toast.success("Facture générée");
    else toast.error(data.error || "Échec de la facturation");
    load(); onChanged && onChanged();
  };

  if (!d) return null;
  const { order, customer, intake, booking, invoice } = d;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex justify-end" onClick={onClose}>
      <div className="bg-[#FCFAF7] w-full max-w-lg h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-order-detail">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl text-[#5A3A4B] flex items-center gap-2">
            {order.service_name}
            {order.service_slug === "sos-sorciere" && <Zap size={18} className="text-[#C9A56A]" />}
          </h2>
          <button onClick={onClose} className="p-2" data-testid="admin-detail-close"><X size={20} /></button>
        </div>

        <span className="badge-soft mb-4 inline-block">{d.session_status_label}</span>

        <div className="space-y-4 text-sm">
          <div className="card-soft p-4">
            <div className="eyebrow mb-2">Cliente</div>
            <p className="text-[#5A3A4B] font-medium">{customer.first_name} {customer.last_name}</p>
            <p className="text-[#7A5D58]">{order.customer_email}</p>
            {customer.phone && <p className="text-[#7A5D58]">{customer.phone}</p>}
            <p className="text-[#7A5D58] mt-1">Réception : {order.delivery_method === "whatsapp" ? "WhatsApp" : order.delivery_method === "email" ? "Email" : "—"}</p>
          </div>

          <div className="card-soft p-4">
            <div className="eyebrow mb-2">Paiement</div>
            <p className="text-[#5A3A4B]">{eur(order.amount_cents)} · {order.payment_status}</p>
            {order.paid_at && <p className="text-[#7A5D58]">Payé le {new Date(order.paid_at).toLocaleString("fr-FR")}</p>}
            {order.stripe_payment_intent_id && <p className="text-[#7A5D58] text-xs break-all mt-1">{order.stripe_payment_intent_id}</p>}
          </div>

          {intake && intake.question_or_intention && (
            <div className="card-soft p-4">
              <div className="eyebrow mb-2">Question / intention</div>
              <p className="text-[#7A5D58] italic">« {intake.question_or_intention} »</p>
            </div>
          )}

          {intake && (intake.date_of_birth || intake.guidance_subject || intake.expectations) && (
            <div className="card-soft p-4" data-testid="admin-guidance-prep">
              <div className="eyebrow mb-2">Préparation guidance</div>
              <div className="space-y-1 text-[#7A5D58]">
                {intake.date_of_birth && <p>Naissance : {intake.date_of_birth}{intake.birth_time_unknown ? " (heure inconnue)" : intake.birth_time ? ` à ${intake.birth_time}` : ""}</p>}
                {(intake.birth_city || intake.birth_country) && <p>Lieu : {[intake.birth_city, intake.birth_country].filter(Boolean).join(", ")}</p>}
                {intake.guidance_subject && <p>Sujet : {intake.guidance_subject}</p>}
                {intake.expectations && <p className="italic">Attentes : « {intake.expectations} »</p>}
                <p className="text-xs text-[#a58f92] mt-1">Accord énergie : {intake.authorization ? "oui" : "—"} · Consentement : {intake.consent ? "oui" : "—"}</p>
              </div>
            </div>
          )}

          {intake && intake.photo_file_id && (
            <div className="card-soft p-4">
              <div className="eyebrow mb-2">Photo (privée)</div>
              <AdminPhoto fileId={intake.photo_file_id} />
            </div>
          )}

          {booking && booking.booking_start && (
            <div className="card-soft p-4">
              <div className="eyebrow mb-2">Créneau</div>
              <p className="text-[#5A3A4B]">{new Date(booking.booking_start).toLocaleString("fr-FR")}</p>
            </div>
          )}

          <div className="card-soft p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="eyebrow">Facturation Henrri</div>
              <span className={`text-xs font-medium ${invoice.invoice_status === "facturée" ? "text-green-700" : invoice.invoice_status === "erreur" ? "text-red-600" : "text-[#8a6a2f]"}`}>
                {invoice.invoice_status || "à_facturer"}
              </span>
            </div>
            {invoice.invoice_number && <p className="text-[#7A5D58]">N° {invoice.invoice_number}</p>}
            {invoice.error_message && <p className="text-red-600 text-xs mt-1">{invoice.error_message}</p>}
            <button onClick={retryInvoice} className="btn-secondary !py-2 !px-4 mt-3 text-xs" data-testid="admin-retry-invoice">
              <RefreshCw size={14} /> Réessayer la facturation
            </button>
          </div>

          {/* Private notes */}
          <div className="card-soft p-4">
            <div className="eyebrow mb-2 flex items-center gap-2"><StickyNote size={14} /> Notes privées (jamais visibles par la cliente)</div>
            <div className="space-y-2 mb-3">
              {d.notes.map((n) => (
                <p key={n.id} className="text-sm text-[#5A3A4B] bg-[#F6EFE7] rounded-lg p-2">{n.note}</p>
              ))}
            </div>
            <textarea rows={2} className="field-input resize-none" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ajouter une note…" data-testid="admin-note-input" />
            <button onClick={addNote} className="btn-secondary !py-2 !px-4 mt-2 text-xs" data-testid="admin-add-note">Ajouter la note</button>
          </div>

          {/* Status actions */}
          <div className="grid grid-cols-1 gap-2">
            <button onClick={() => setStatus("in_progress")} className="btn-secondary" data-testid="status-in-progress">Marquer comme en cours</button>
            <button onClick={() => setStatus("report_sent")} className="btn-secondary" data-testid="status-report-sent">Compte rendu envoyé</button>
            <button onClick={() => setStatus("completed")} className="btn-primary" data-testid="status-completed"><CheckCircle2 size={16} /> Terminer la séance</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  usePageTitle("Administration");
  const [authed, setAuthed] = useState(!!getAdminToken());
  const [view, setView] = useState("dashboard");
  const [kpi, setKpi] = useState({});
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("all");
  const [services, setServices] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invTab, setInvTab] = useState("all");
  const [customers, setCustomers] = useState([]);
  const [newsletter, setNewsletter] = useState({ total: 0, active: 0, subscribers: [] });
  const [campaigns, setCampaigns] = useState([]);
  const [compose, setCompose] = useState({ subject: "", title: "La Guidance de la Semaine ✨", content: "" });
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [settings, setSettings] = useState({});
  const [conformite, setConformite] = useState(null);
  const [integrations, setIntegrations] = useState(null);
  const [reclamations, setReclamations] = useState([]);
  const [retractations, setRetractations] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [guideData, setGuideData] = useState({ sales_count: 0, orders: [] });
  const [selected, setSelected] = useState(null);

  const loadDashboard = () => api.get("/admin/dashboard").then((r) => setKpi(r.data)).catch(() => setAuthed(false));
  const loadOrders = (f) => api.get(`/admin/orders?filter=${f}`).then((r) => setOrders(r.data));
  const loadServices = () => api.get("/admin/services").then((r) => setServices(r.data));
  const loadInvoices = (t) => api.get(`/admin/invoices?tab=${t}`).then((r) => setInvoices(r.data));
  const loadCustomers = () => api.get("/admin/customers").then((r) => setCustomers(r.data));
  const loadSettings = () => api.get("/admin/settings").then((r) => setSettings(r.data));

  useEffect(() => {
    if (!authed) return;
    loadDashboard();
  }, [authed]);

  useEffect(() => {
    if (!authed) return;
    if (view === "dashboard") loadDashboard();
    if (["orders", "sos", "guidance", "energetique"].includes(view)) {
      const f = view === "orders" ? orderFilter : view;
      loadOrders(f);
    }
    if (view === "services") loadServices();
    if (view === "invoices") loadInvoices(invTab);
    if (view === "customers") loadCustomers();
    if (view === "newsletter") {
      api.get("/admin/newsletter/subscribers").then((r) => setNewsletter(r.data));
      api.get("/admin/newsletter/campaigns").then((r) => setCampaigns(r.data));
      loadSettings();
    }
    if (view === "settings") loadSettings();
    if (view === "conformite") api.get("/admin/conformite").then((r) => setConformite(r.data));
    if (view === "integrations") api.get("/admin/integrations").then((r) => setIntegrations(r.data));
    if (view === "reclamations") api.get("/admin/reclamations").then((r) => setReclamations(r.data));
    if (view === "retractations") api.get("/admin/retractations").then((r) => setRetractations(r.data));
    if (view === "subscriptions") api.get("/admin/subscriptions").then((r) => setSubscriptions(r.data));
    if (view === "guide") { api.get("/admin/guide/orders").then((r) => setGuideData(r.data)); loadSettings(); }
  }, [view, orderFilter, invTab, authed]);

  if (!authed) return <Login onLogin={() => setAuthed(true)} />;

  const NAV = [
    { k: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
    { k: "orders", label: "Commandes", icon: ShoppingBag },
    { k: "sos", label: "SOS Sorcière", icon: Zap },
    { k: "guidance", label: "Guidances", icon: Sparkles },
    { k: "energetique", label: "Énergétique", icon: HeartHandshake },
    { k: "subscriptions", label: "Entre Lui et Moi", icon: Mail },
    { k: "guide", label: "Grimoire Tarot", icon: FileText },
    { k: "invoices", label: "Facturation", icon: FileText },
    { k: "customers", label: "Clientes", icon: Users },
    { k: "newsletter", label: "Newsletter", icon: Mail },
    { k: "reclamations", label: "Réclamations", icon: MessageSquareWarning },
    { k: "retractations", label: "Rétractations", icon: Undo2 },
    { k: "services", label: "Prestations", icon: SettingsIcon },
    { k: "integrations", label: "Intégrations", icon: Plug },
    { k: "conformite", label: "Conformité", icon: ClipboardCheck },
    { k: "settings", label: "Paramètres", icon: SettingsIcon },
  ];

  const saveService = async (s) => {
    await api.put(`/admin/services/${s.slug}`, {
      name: s.name, subtitle: s.subtitle, price_cents: parseInt(s.price_cents),
      duration_minutes: s.duration_minutes ? parseInt(s.duration_minutes) : null,
      booking_required: s.booking_required, cal_event_url: s.cal_event_url,
      active: s.active, sort_order: parseInt(s.sort_order), badge: s.badge || "",
    });
    toast.success("Prestation enregistrée"); loadServices();
  };

  const saveSettings = async () => {
    await api.put("/admin/settings", settings);
    toast.success("Paramètres enregistrés");
  };

  const sendNewsletter = async (test) => {
    if (!compose.subject.trim() || !compose.content.trim()) {
      toast.error("Le sujet et le contenu sont requis."); return;
    }
    if (!test && !window.confirm(`Envoyer définitivement la newsletter à ${newsletter.active} abonné(s) actif(s) ?`)) return;
    setSending(true);
    try {
      const body = test ? { ...compose, test_email: settings.contact_email } : compose;
      const { data } = await api.post("/admin/newsletter/send", body);
      toast.success(test ? "Email de test envoyé" : `Newsletter envoyée à ${data.recipients_count} abonné(s) ✨`);
      if (!test) {
        setCompose({ subject: "", title: "La Guidance de la Semaine ✨", content: "" });
        setShowPreview(false);
        api.get("/admin/newsletter/campaigns").then((r) => setCampaigns(r.data));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-grain flex">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-60 bg-[#5A3A4B] text-[#F6EFE7] p-4 shrink-0">
        <div className="flex items-center gap-2 px-2 py-3 mb-4">
          <img src="/brand-logo.png" alt="Logo" className="w-9 h-9 rounded-full bg-[#F6EFE7]" />
          <span className="font-serif text-sm leading-tight">Administration</span>
        </div>
        <nav className="flex-1 space-y-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <button
                key={n.k}
                onClick={() => setView(n.k)}
                data-testid={`admin-nav-${n.k}`}
                className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition ${
                  view === n.k ? "bg-[#F6EFE7]/15 text-[#F6EFE7]" : "text-[#F6EFE7]/70 hover:text-[#F6EFE7]"
                }`}
              >
                <Icon size={17} /> {n.label}
              </button>
            );
          })}
        </nav>
        <button onClick={() => { clearAdminToken(); setAuthed(false); }} className="flex items-center gap-2 text-sm text-[#F6EFE7]/70 hover:text-[#F6EFE7] px-3 py-2" data-testid="admin-logout">
          <LogOut size={16} /> Déconnexion
        </button>
      </aside>

      {/* Mobile nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-[#5A3A4B] text-[#F6EFE7] px-3 py-2 flex gap-2 overflow-x-auto">
        {NAV.map((n) => (
          <button key={n.k} onClick={() => setView(n.k)} className={`text-xs whitespace-nowrap px-3 py-1.5 rounded-full ${view === n.k ? "bg-[#F6EFE7]/20" : ""}`}>
            {n.label}
          </button>
        ))}
      </div>

      <main className="flex-1 p-5 sm:p-8 pt-16 md:pt-8 overflow-x-hidden">
        {view === "dashboard" && (
          <div data-testid="admin-dashboard">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-serif text-3xl text-[#5A3A4B]">Tableau de bord</h1>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <Kpi label="Chiffre d'affaires du mois" value={eur(kpi.ca_month_cents)} accent />
              <Kpi label="Ventes du mois" value={kpi.ventes_month ?? 0} />
              <Kpi label="Séances aujourd'hui" value={kpi.seances_today ?? 0} />
              <Kpi label="Séances cette semaine" value={kpi.seances_week ?? 0} />
              <Kpi label="SOS Sorcière en attente" value={kpi.sos_pending ?? 0} />
              <Kpi label="Infos à compléter" value={kpi.infos_a_completer ?? 0} />
              <Kpi label="Factures à générer" value={kpi.factures_a_generer ?? 0} />
              <Kpi label="Erreurs de facturation" value={kpi.erreurs_facturation ?? 0} />
              <Kpi label="Séances terminées" value={kpi.seances_terminees ?? 0} />
            </div>
          </div>
        )}

        {["orders", "sos", "guidance", "energetique"].includes(view) && (
          <div data-testid="admin-orders">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">
              {view === "orders" ? "Commandes" : view === "sos" ? "SOS Sorcière" : view === "guidance" ? "Guidances" : "Énergétique"}
            </h1>
            {view === "orders" && (
              <div className="flex gap-2 overflow-x-auto pb-3 mb-4">
                {[["all", "Toutes"], ["today", "Aujourd'hui"], ["week", "Cette semaine"], ["paid", "Payées"], ["info_pending", "Infos à compléter"], ["booked", "Réservées"], ["completed", "Terminées"]].map(([k, l]) => (
                  <button key={k} onClick={() => setOrderFilter(k)} className={`text-xs whitespace-nowrap rounded-full px-3 py-1.5 ${orderFilter === k ? "bg-[#5A3A4B] text-white" : "bg-white border border-[#7A5D58]/15 text-[#5A3A4B]"}`}>
                    {l}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-3">
              {orders.length === 0 && <p className="text-[#7A5D58]">Aucune commande.</p>}
              {orders.map((o) => (
                <button
                  key={o.order_id}
                  onClick={() => setSelected(o.order_id)}
                  data-testid={`admin-order-${o.order_id}`}
                  className="card-soft card-hover w-full text-left p-4 flex flex-wrap items-center justify-between gap-3"
                >
                  <div>
                    <p className="font-medium text-[#5A3A4B]">{o.first_name} {o.last_name} — {o.service_name}</p>
                    <p className="text-xs text-[#7A5D58]">{o.email} · {eur(o.amount_cents)} · {o.paid_at ? new Date(o.paid_at).toLocaleDateString("fr-FR") : "non payé"}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {o.invoice_status && <span className={`text-xs ${o.invoice_status === "erreur" ? "text-red-600" : o.invoice_status === "facturée" ? "text-green-700" : "text-[#8a6a2f]"}`}>{o.invoice_status}</span>}
                    <span className="badge-soft">{o.session_status_label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {view === "invoices" && (
          <div data-testid="admin-invoices">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Facturation</h1>
            <div className="flex gap-2 mb-4">
              {[["all", "Toutes"], ["a_facturer", "À facturer"], ["facturees", "Facturées"], ["erreurs", "Erreurs"]].map(([k, l]) => (
                <button key={k} onClick={() => setInvTab(k)} className={`text-xs rounded-full px-3 py-1.5 ${invTab === k ? "bg-[#5A3A4B] text-white" : "bg-white border border-[#7A5D58]/15 text-[#5A3A4B]"}`}>{l}</button>
              ))}
            </div>
            <div className="space-y-3">
              {invoices.length === 0 && <p className="text-[#7A5D58]">Aucune facture.</p>}
              {invoices.map((inv) => (
                <div key={inv.order_id} className="card-soft p-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#5A3A4B]">{inv.customer_name} — {inv.service_name}</p>
                    <p className="text-xs text-[#7A5D58]">{eur(inv.amount_cents)} · {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString("fr-FR") : "—"} · {inv.invoice_number || "—"}</p>
                    {inv.error_message && <p className="text-xs text-red-600">{inv.error_message}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium ${inv.invoice_status === "erreur" ? "text-red-600" : inv.invoice_status === "facturée" ? "text-green-700" : "text-[#8a6a2f]"}`}>{inv.invoice_status}</span>
                    <button onClick={async () => { const { data } = await api.post(`/admin/invoices/${inv.order_id}/retry`); data.ok ? toast.success("Facture générée") : toast.error(data.error || "Échec"); loadInvoices(invTab); }} className="btn-secondary !py-1.5 !px-3 text-xs">
                      <RefreshCw size={12} /> Réessayer
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "customers" && (
          <div data-testid="admin-customers">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Clientes</h1>
            <div className="space-y-3">
              {customers.map((c, i) => (
                <div key={i} className="card-soft p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium text-[#5A3A4B]">{c.first_name} {c.last_name}</p>
                    <p className="text-xs text-[#7A5D58]">{c.email} {c.phone ? `· ${c.phone}` : ""}</p>
                  </div>
                  <span className="badge-soft">{c.orders_count} commande{c.orders_count > 1 ? "s" : ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "newsletter" && (
          <div data-testid="admin-newsletter">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">La Guidance de la Semaine ✨</h1>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6 max-w-xl">
              <Kpi label="Abonnés" value={newsletter.total} accent />
              <Kpi label="Abonnés actifs" value={newsletter.active} />
              <Kpi label="Désinscrits" value={newsletter.total - newsletter.active} />
            </div>

            <div className="card-soft p-5 max-w-3xl mb-6">
              <div className="eyebrow mb-3">Écrire une newsletter</div>
              <div className="space-y-3">
                <div>
                  <label className="field-label text-xs">Sujet de l'email</label>
                  <input className="field-input" value={compose.subject} onChange={(e) => setCompose({ ...compose, subject: e.target.value })} data-testid="nl-subject" placeholder="La Guidance de la Semaine — énergie du moment ✨" />
                </div>
                <div>
                  <label className="field-label text-xs">Titre affiché</label>
                  <input className="field-input" value={compose.title} onChange={(e) => setCompose({ ...compose, title: e.target.value })} data-testid="nl-title" />
                </div>
                <div>
                  <label className="field-label text-xs">Contenu (sauts de ligne conservés)</label>
                  <textarea rows={10} className="field-input resize-none" value={compose.content} onChange={(e) => setCompose({ ...compose, content: e.target.value })} data-testid="nl-content" placeholder="Écris ici ta guidance de la semaine…" />
                </div>
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setShowPreview((v) => !v)} className="btn-secondary !py-2 !px-4 text-xs" data-testid="nl-preview-btn">
                    {showPreview ? "Masquer l'aperçu" : "Aperçu"}
                  </button>
                  <button onClick={() => sendNewsletter(true)} disabled={sending} className="btn-secondary !py-2 !px-4 text-xs" data-testid="nl-test-btn">
                    Envoi de test (à moi)
                  </button>
                  <button onClick={() => sendNewsletter(false)} disabled={sending} className="btn-primary !py-2 !px-5 text-xs" data-testid="nl-send-btn">
                    {sending ? "Envoi…" : `Envoyer à ${newsletter.active} abonné(s)`}
                  </button>
                </div>
                {showPreview && (
                  <div className="rounded-xl border border-[#7A5D58]/15 bg-[#F6EFE7] p-5" data-testid="nl-preview">
                    <div className="eyebrow mb-2 text-center">{compose.title}</div>
                    <p className="text-sm text-[#5A3A4B] whitespace-pre-line leading-relaxed">{compose.content || "…"}</p>
                    <p className="text-xs text-[#8A6D74] border-t border-[#C9A56A]/35 mt-4 pt-3">Tu reçois cet e-mail car tu es inscrite à La Guidance de la Semaine. Se désinscrire.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-6 max-w-5xl">
              <div className="card-soft p-5">
                <div className="eyebrow mb-3">Abonnés ({newsletter.total})</div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {newsletter.subscribers.length === 0 && <p className="text-sm text-[#7A5D58]">Aucun abonné pour l'instant.</p>}
                  {newsletter.subscribers.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border-b border-[#7A5D58]/10 py-1.5 last:border-0">
                      <div>
                        <span className="text-[#5A3A4B] font-medium">{s.first_name || "—"}</span>
                        <span className="text-[#7A5D58]"> · {s.email}</span>
                      </div>
                      <span className={`text-xs ${s.status === "active" ? "text-green-700" : "text-[#a58f92]"}`}>
                        {s.status === "active" ? "actif" : "désinscrit"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="card-soft p-5">
                <div className="eyebrow mb-3">Historique des envois</div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {campaigns.length === 0 && <p className="text-sm text-[#7A5D58]">Aucun envoi pour l'instant.</p>}
                  {campaigns.map((c) => (
                    <div key={c.id} className="text-sm border-b border-[#7A5D58]/10 py-2 last:border-0">
                      <p className="text-[#5A3A4B] font-medium">{c.subject}</p>
                      <p className="text-xs text-[#7A5D58]">{c.sent_at ? new Date(c.sent_at).toLocaleString("fr-FR") : "—"} · {c.recipients_count} destinataire(s)</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {view === "services" && (
          <div data-testid="admin-services">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Prestations</h1>
            <div className="space-y-4">
              {services.map((s, idx) => (
                <div key={s.slug} className="card-soft p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-serif text-lg text-[#5A3A4B]">{s.name}</span>
                    <label className="flex items-center gap-2 text-xs text-[#7A5D58]">
                      <input type="checkbox" className="w-4 h-4 accent-[#5A3A4B]" checked={s.active} onChange={(e) => { const c = [...services]; c[idx] = { ...s, active: e.target.checked }; setServices(c); }} />
                      Active
                    </label>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div><label className="field-label text-xs">Nom</label><input className="field-input" value={s.name} onChange={(e) => { const c = [...services]; c[idx] = { ...s, name: e.target.value }; setServices(c); }} /></div>
                    <div><label className="field-label text-xs">Prix (centimes)</label><input className="field-input" value={s.price_cents} onChange={(e) => { const c = [...services]; c[idx] = { ...s, price_cents: e.target.value }; setServices(c); }} /></div>
                    <div className="sm:col-span-2"><label className="field-label text-xs">Sous-titre</label><input className="field-input" value={s.subtitle || ""} onChange={(e) => { const c = [...services]; c[idx] = { ...s, subtitle: e.target.value }; setServices(c); }} /></div>
                    <div><label className="field-label text-xs">Durée (min)</label><input className="field-input" value={s.duration_minutes || ""} onChange={(e) => { const c = [...services]; c[idx] = { ...s, duration_minutes: e.target.value }; setServices(c); }} /></div>
                    <div className="flex items-end"><label className="flex items-center gap-2 text-sm text-[#7A5D58]"><input type="checkbox" className="w-4 h-4 accent-[#5A3A4B]" checked={s.booking_required} onChange={(e) => { const c = [...services]; c[idx] = { ...s, booking_required: e.target.checked }; setServices(c); }} /> Réservation requise</label></div>
                    <div className="sm:col-span-2"><label className="field-label text-xs">Lien Cal.com</label><input className="field-input" value={s.cal_event_url || ""} placeholder="https://cal.com/..." onChange={(e) => { const c = [...services]; c[idx] = { ...s, cal_event_url: e.target.value }; setServices(c); }} /></div>
                  </div>
                  <button onClick={() => saveService(s)} className="btn-primary !py-2 !px-5 mt-3 text-xs" data-testid={`admin-save-service-${s.slug}`}>Enregistrer</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "subscriptions" && (
          <div data-testid="admin-subscriptions">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Entre Lui et Moi — Abonnées</h1>
            {subscriptions.length === 0 && <p className="text-[#7A5D58]">Aucune abonnée pour le moment.</p>}
            <div className="space-y-4">
              {subscriptions.map((s) => (
                <div key={s.id} className="card-soft p-5" data-testid={`sub-${s.id}`}>
                  <div className="flex flex-wrap justify-between gap-3">
                    <div>
                      <h3 className="font-serif text-lg text-[#5A3A4B]">{s.first_name} {s.last_name}</h3>
                      <p className="text-sm text-[#7A5D58]">{s.email}</p>
                    </div>
                    <span className={s.status === "active" ? "badge-gold" : "badge-soft"}>
                      {s.status}{s.cancel_at_period_end ? " · résiliation programmée" : ""}
                    </span>
                  </div>
                  <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-[#7A5D58]">
                    <p><strong>Adresse :</strong> {s.address} {s.address_complement}, {s.postal_code} {s.city}, {s.country}</p>
                    <p><strong>Naissance :</strong> {s.date_of_birth || "—"} · <strong>Signe :</strong> {s.zodiac || "—"}</p>
                    <p><strong>Abonnée le :</strong> {s.created_at ? new Date(s.created_at).toLocaleDateString("fr-FR") : "—"}</p>
                    <p><strong>Prochaine échéance :</strong> {s.current_period_end ? new Date(s.current_period_end).toLocaleDateString("fr-FR") : "—"}</p>
                  </div>
                  {s.private_message && (
                    <p className="mt-2 text-sm text-[#5A3A4B] bg-[#FCFAF7] rounded-xl p-3 italic">« {s.private_message} »</p>
                  )}
                  <div className="mt-4">
                    <p className="eyebrow mb-2">Courriers mensuels</p>
                    {s.cycles.length === 0 && <p className="text-xs text-[#7A5D58]">Aucun paiement encore.</p>}
                    <div className="space-y-2">
                      {s.cycles.map((c) => (
                        <div key={c.order_id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-t border-[#7A5D58]/10 pt-2">
                          <span className="text-[#7A5D58]">{c.paid_at ? new Date(c.paid_at).toLocaleDateString("fr-FR") : "—"} · {c.order_number}</span>
                          <select
                            className="field-input !py-1.5 !w-auto text-sm"
                            value={c.courrier_status}
                            onChange={async (e) => { await api.post(`/admin/subscriptions/cycle/${c.order_id}/courrier`, { status: e.target.value }); api.get("/admin/subscriptions").then((r) => setSubscriptions(r.data)); }}
                            data-testid={`courrier-status-${c.order_id}`}
                          >
                            <option value="a_preparer">À préparer</option>
                            <option value="en_preparation">En préparation</option>
                            <option value="envoye">Envoyé</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "guide" && (
          <div data-testid="admin-guide">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Grimoire Visuel du Tarot</h1>
            <div className="card-soft p-5 grid sm:grid-cols-2 gap-4 max-w-3xl mb-6">
              <div className="sm:col-span-2 text-sm text-[#7A5D58]">Ventes payées : <strong>{guideData.sales_count}</strong></div>
              {[
                ["guide_title", "Titre", "text"], ["guide_price_normal_cents", "Prix normal (centimes)", "number"],
                ["guide_price_promo_cents", "Prix promo (centimes)", "number"],
                ["guide_promo_start", "Début promo (AAAA-MM-JJ)", "text"], ["guide_promo_end", "Fin promo (AAAA-MM-JJ)", "text"],
                ["guide_cover_image", "Image de couverture (URL)", "text"], ["guide_access_link", "Lien d'accès au Guide (dossier)", "text"],
              ].map(([k, l, t]) => (
                <div key={k} className={["guide_title", "guide_access_link"].includes(k) ? "sm:col-span-2" : ""}>
                  <label className="field-label text-xs">{l}</label>
                  <input type={t} className="field-input" value={settings[k] ?? ""} onChange={(e) => setSettings({ ...settings, [k]: t === "number" ? Number(e.target.value) : e.target.value })} data-testid={`setting-${k}`} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="field-label text-xs">Description</label>
                <textarea rows={3} className="field-input resize-none" value={settings.guide_description || ""} onChange={(e) => setSettings({ ...settings, guide_description: e.target.value })} data-testid="setting-guide_description" />
              </div>
              <label className="flex items-center gap-2 text-sm text-[#5A3A4B]"><input type="checkbox" checked={settings.guide_promo_active ?? false} onChange={(e) => setSettings({ ...settings, guide_promo_active: e.target.checked })} data-testid="setting-guide_promo_active" /> Promotion active</label>
              <label className="flex items-center gap-2 text-sm text-[#5A3A4B]"><input type="checkbox" checked={settings.guide_active ?? false} onChange={(e) => setSettings({ ...settings, guide_active: e.target.checked })} data-testid="setting-guide_active" /> Produit actif</label>
              <div className="sm:col-span-2">
                <button onClick={saveSettings} className="btn-primary" data-testid="admin-save-guide">Enregistrer</button>
                {!settings.guide_access_link && <p className="mt-2 text-xs text-[#8a6a2f]">Le lien d'accès est vide : les acheteuses verront « Ton guide sera disponible ici très bientôt ». Colle ici le lien du dossier quand il sera prêt.</p>}
              </div>
            </div>
            <h2 className="font-serif text-xl text-[#5A3A4B] mb-3">Commandes</h2>
            <div className="space-y-2">
              {guideData.orders.length === 0 && <p className="text-[#7A5D58]">Aucune commande.</p>}
              {guideData.orders.map((o) => (
                <div key={o.order_id} className="card-soft p-4 flex flex-wrap justify-between gap-2 text-sm">
                  <span className="text-[#5A3A4B]">{o.email}</span>
                  <span className="text-[#7A5D58]">{(o.amount_cents / 100).toFixed(2)} € · {o.payment_status} · facture {o.invoice_status || "—"} {o.invoice_number || ""}</span>
                </div>
              ))}
            </div>
          </div>
        )}


        {view === "settings" && (
          <div data-testid="admin-settings">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Paramètres & identité légale</h1>
            <div className="card-soft p-5 grid sm:grid-cols-2 gap-4 max-w-3xl">
              {[
                ["signature_phrase", "Phrase-signature (positionnement)"],
                ["contact_email", "Email de contact"], ["instagram_url", "URL Instagram"],
                ["instagram_handle", "Nom Instagram affiché"], ["whatsapp_contact", "Contact WhatsApp"],
                ["business_name", "Nom légal"], ["commercial_name", "Nom commercial déclaré"],
                ["business_legal_form", "Forme juridique"], ["business_phone", "Téléphone professionnel"],
                ["business_address", "Adresse professionnelle"],
                ["business_postal_code", "Code postal"], ["business_city", "Ville"],
                ["business_country", "Pays"], ["siren", "SIREN"], ["siret", "SIRET"], ["rne", "RNE"],
                ["vat_status", "Statut TVA"], ["vat_number", "N° TVA"],
                ["host_name", "Hébergeur — raison sociale"], ["host_address", "Hébergeur — adresse"],
                ["host_phone", "Hébergeur — téléphone"], ["host_url", "Hébergeur — site"],
                ["mediator_name", "Médiateur — nom"], ["mediator_email", "Médiateur — email"],
                ["mediator_phone", "Médiateur — téléphone"], ["mediator_url", "Médiateur — site"],
                ["mediator_address", "Médiateur — adresse"], ["cal_main_url", "Cal.com — compte principal"],
              ].map(([k, l]) => (
                <div key={k} className={["mediator_address", "signature_phrase"].includes(k) ? "sm:col-span-2" : ""}>
                  <label className="field-label text-xs">{l}</label>
                  <input className="field-input" value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })} data-testid={`setting-${k}`} />
                </div>
              ))}
              <div className="sm:col-span-2">
                <button onClick={saveSettings} className="btn-primary" data-testid="admin-save-settings">Enregistrer les paramètres</button>
              </div>
            </div>
          </div>
        )}
        {view === "reclamations" && (
          <div data-testid="admin-reclamations">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Réclamations</h1>
            <div className="space-y-3">
              {reclamations.length === 0 && <p className="text-[#7A5D58]">Aucune réclamation.</p>}
              {reclamations.map((r) => (
                <div key={r.id} className="card-soft p-4">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <p className="font-medium text-[#5A3A4B]">{r.first_name} {r.last_name} — {r.subject}</p>
                    <select value={r.status} onChange={async (e) => { await api.post(`/admin/reclamations/${r.id}/status`, { status: e.target.value }); api.get("/admin/reclamations").then((x) => setReclamations(x.data)); }} className="field-input !py-1 !px-2 text-xs w-auto">
                      {["Reçue", "En cours", "Répondue", "Clôturée"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <p className="text-xs text-[#7A5D58]">{r.email} · {r.order_number || "—"} · {new Date(r.created_at).toLocaleString("fr-FR")}</p>
                  <p className="text-sm text-[#5A3A4B] mt-2 bg-[#F6EFE7] rounded-lg p-2">{r.message}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "retractations" && (
          <div data-testid="admin-retractations">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Demandes de rétractation</h1>
            <div className="space-y-3">
              {retractations.length === 0 && <p className="text-[#7A5D58]">Aucune demande.</p>}
              {retractations.map((r) => (
                <div key={r.id} className="card-soft p-4 flex items-center justify-between gap-3">
                  <div>
                    <p className="font-medium text-[#5A3A4B]">{r.first_name} {r.last_name}</p>
                    <p className="text-xs text-[#7A5D58]">{r.email} · {r.order_number || "—"} · {r.service_name || "—"} · {new Date(r.created_at).toLocaleString("fr-FR")}</p>
                  </div>
                  <select value={r.status} onChange={async (e) => { await api.post(`/admin/retractations/${r.id}/status`, { status: e.target.value }); api.get("/admin/retractations").then((x) => setRetractations(x.data)); }} className="field-input !py-1 !px-2 text-xs w-auto">
                    {["Reçue", "En cours", "Répondue", "Clôturée"].map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {view === "integrations" && integrations && (
          <div data-testid="admin-integrations">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-5">Intégrations</h1>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
              {[
                ["Stripe", integrations.stripe.status, `Mode : ${integrations.stripe.mode}`],
                ["Henrri", integrations.henrri.status, `Mode : ${integrations.henrri.mode}`],
                ["Cal.com", integrations.calcom.status, integrations.calcom.main_url],
                ["Resend", integrations.resend.status, "Envoi des e-mails"],
              ].map(([name, status, sub]) => (
                <div key={name} className="card-soft p-5">
                  <div className="flex items-center gap-2 mb-1"><Plug size={16} className="text-[#C9A56A]" /><span className="font-medium text-[#5A3A4B]">{name}</span></div>
                  <p className={`text-sm font-medium ${/Connect|Configur/.test(status) ? "text-green-700" : "text-[#8a6a2f]"}`}>{status}</p>
                  <p className="text-xs text-[#7A5D58] mt-1 break-all">{sub}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-[#a58f92] mt-4">Les clés API ne sont jamais affichées — seul l'état de configuration est visible. Aucun passage automatique en production.</p>
          </div>
        )}

        {view === "conformite" && conformite && (
          <div data-testid="admin-conformite">
            <h1 className="font-serif text-3xl text-[#5A3A4B] mb-2">Conformité</h1>
            <p className={`text-sm mb-5 ${conformite.ready ? "text-green-700" : "text-[#8a6a2f]"}`}>
              {conformite.ready ? "✅ Toutes les informations obligatoires sont complétées." : "⚠️ Des informations doivent être complétées avant publication."}
            </p>
            <div className="card-soft p-5 max-w-xl space-y-2">
              {conformite.items.map((it) => (
                <div key={it.label} className="flex items-center justify-between py-1.5 border-b border-[#7A5D58]/10 last:border-0">
                  <span className="text-sm text-[#5A3A4B]">{it.label}</span>
                  <span>{it.ok ? <CheckCircle2 size={18} className="text-green-600" /> : <span className="text-[#8a6a2f] text-sm">⚠️ à compléter</span>}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {selected && <OrderDetail orderId={selected} onClose={() => setSelected(null)} onChanged={() => { loadOrders(view === "orders" ? orderFilter : view); loadDashboard(); }} />}
    </div>
  );
}
